import axios from 'axios';

// Default formats if API call fails or before it completes
const defaultFormats = {
  DateFormat: 'DD/MM/YYYY',
  TimeFormat: 'hh:mm:ss a',
  DateTimeFormat: 'DD/MM/YYYY hh:mm:ss a',
  AmountFormat: '#,###,###,##0.00',
  PriceFormat: '#,###,###,##0.00',
  QuantityFormat: '#,###,###,##0.000',
};

let formatConfig = { ...defaultFormats };

/**
 * Fetches and sets the format configurations from the API
 * @param {string} tenantId - The tenant ID to fetch configurations for
 */
export async function initializeFormats(tenantId) {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No authentication token found, using default formats');
      return;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const response = await axios.get(
      `${baseUrl}conductor/app-config/tenant/${tenantId}`, 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (response.data && Array.isArray(response.data)) {
      response.data.forEach(config => {
        if (config.appConfigP && config.appValue1) {
          formatConfig[config.appConfigP] = config.appValue1;
        }
      });
    }

    console.log('Format config loaded:', formatConfig);
  } catch (error) {
    console.error('Error fetching format configurations, using defaults:', error);
  }
}

/**
 * Parses number format string and applies it to a number
 * @param {number} value - The number to format
 * @param {string} formatStr - Format string like "#,###,###,##0.00"
 * @returns {string} Formatted number string
 */
function applyNumberFormat(value, formatStr) {
  if (isNaN(value) || value === null || value === undefined) return '';

  // Convert to number and handle precision issues
  let numValue = parseFloat(value);
  if (isNaN(numValue)) return '';

  // Handle negative numbers
  const isNegative = numValue < 0;
  const absValue = Math.abs(numValue);

  // Parse format string to determine decimal places
  const formatParts = formatStr.split(';');
  const positiveFormat = formatParts[0];
  const parts = positiveFormat.split('.');
  const decimalPlaces = parts.length > 1 ? parts[1].replace(/[^0]/g, '').length : 0;

  // Round the number to avoid floating point precision issues
  const roundedValue = Math.round(absValue * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);

  // Format the number with appropriate decimal places
  const formattedNumber = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    useGrouping: true
  }).format(roundedValue);

  // Handle negative numbers based on format
  if (isNegative) {
    // Check if format has custom negative handling
    if (formatParts.length > 1) {
      // Custom negative format exists
      const negativeFormat = formatParts[1];
      if (negativeFormat.includes('(') && negativeFormat.includes(')')) {
        return `(${formattedNumber})`;
      } else if (negativeFormat.toLowerCase().includes('cr')) {
        return `CR ${formattedNumber}`;
      } else {
        // Parse the negative format to see if it should show minus sign
        if (negativeFormat.includes('-')) {
          return `-${formattedNumber}`;
        } else {
          // Just remove the minus sign if format doesn't show it
          return formattedNumber;
        }
      }
    } else {
      // Default negative format
      return `-${formattedNumber}`;
    }
  }

  return formattedNumber;
}

/**
 * Formats a date according to the configured DateFormat
 * @param {Date|string} date - The date to format
 * @param {string} [format] - Optional format string (defaults to formatConfig.DateFormat)
 * @returns {string} Formatted date string
 */
export function formatDate(date, format) {
  if (!date) return '';
  
  let d;
  
  // Check if date is already in DD/MM/YYYY format
  if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
    const [day, month, year] = date.split('/').map(Number);
    d = new Date(year, month - 1, day);
  } else {
    d = date instanceof Date ? date : new Date(date);
  }
  
  if (isNaN(d.getTime())) return date || ''; // Return original value if invalid date
  
  const formatStr = format || (formatConfig && formatConfig.DateFormat) || 'DD/MM/YYYY';

  // Extract components
  const pad = (n) => n.toString().padStart(2, '0');

  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();

  // Create tokens map for date formatting
  const tokens = {
    'YYYY': year,
    'YY': year.toString().slice(-2),
    'MM': pad(month),
    'M': month,
    'DD': pad(day),
    'D': day,
    'MMMM': new Intl.DateTimeFormat('en-US', { month: 'long' }).format(d),
    'MMM': new Intl.DateTimeFormat('en-US', { month: 'short' }).format(d),
  };

  // Replace tokens in format string
  return formatStr.replace(/(YYYY|YY|MMMM|MMM|MM|M|DD|D)/g, (match) => tokens[match] || match);
}

/**
 * Parses a value to (year, month, day) in a timezone-neutral way (calendar date only).
 * Used for "document date" etc. that must not change with user timezone.
 * @returns {{ year: number, month: number, day: number } | null}
 */
function parseDateOnly(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
    };
  }
  const s = String(value).trim();
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return {
      year: parseInt(isoMatch[1], 10),
      month: parseInt(isoMatch[2], 10),
      day: parseInt(isoMatch[3], 10),
    };
  }
  const dmyMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmyMatch) {
    return {
      year: parseInt(dmyMatch[3], 10),
      month: parseInt(dmyMatch[2], 10),
      day: parseInt(dmyMatch[1], 10),
    };
  }
  return null;
}

/**
 * Formats a date-only value (e.g. document date) without timezone conversion.
 * The same calendar date is shown in every timezone.
 * @param {Date|string} date - The date to format (ISO YYYY-MM-DD, DD/MM/YYYY, or Date)
 * @param {string} [format] - Optional format string (defaults to formatConfig.DateFormat)
 * @returns {string} Formatted date string
 */
export function formatDateOnly(date, format) {
  const parsed = parseDateOnly(date);
  if (!parsed) return date ? formatDate(date, format) : '';
  const { year, month, day } = parsed;
  const d = new Date(year, month - 1, day);
  if (isNaN(d.getTime())) return '';
  return formatDate(d, format);
}

/**
 * Formats a date and time according to the configured DateTimeFormat
 * @param {Date|string} date - The date to format
 * @param {string} [format] - Optional format string (defaults to formatConfig.DateTimeFormat)
 * @returns {string} Formatted date and time string
 */
export function formatDateTime(date, format) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const formatStr = format || formatConfig.DateTimeFormat || 'DD/MM/YYYY hh:mm:ss a';
  
  // Extract components
  const pad = (n) => n.toString().padStart(2, '0');
  const pad3 = (n) => n.toString().padStart(3, '0');
  
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();
  const milliseconds = d.getMilliseconds();
  const dayOfWeek = d.getDay();
  
  // 12-hour format
  const h = hours % 12 || 12;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  // Create tokens map
  const tokens = {
    // Year
    'YYYY': year,
    'YY': year.toString().slice(-2),
    
    // Month
    'MM': pad(month),
    'M': month,
    'MMMM': new Intl.DateTimeFormat('en-US', { month: 'long' }).format(d),
    'MMM': new Intl.DateTimeFormat('en-US', { month: 'short' }).format(d),
    
    // Day of month
    'DD': pad(day),
    'D': day,
    'Do': day + (day % 10 === 1 && day !== 11 ? 'st' :
                day % 10 === 2 && day !== 12 ? 'nd' :
                day % 10 === 3 && day !== 13 ? 'rd' : 'th'),
    
    // Day of week
    'dddd': ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek],
    'ddd': ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek],
    'd': dayOfWeek,
    
    // Hours
    'HH': pad(hours),
    'H': hours,
    'hh': pad(h),
    'h': h,
    
    // Minutes
    'mm': pad(minutes),
    'm': minutes,
    
    // Seconds
    'ss': pad(seconds),
    's': seconds,
    
    // Milliseconds
    'SSS': pad3(milliseconds),
    'SS': pad3(milliseconds).slice(0, 2),
    'S': milliseconds,
    
    // AM/PM
    'A': ampm.toUpperCase(),
    'a': ampm.toLowerCase(),
  };
  
  // Replace tokens in format string
  return formatStr.replace(/(\[[^\]]+\]|Y{4}|Y{2}|M{4}|M{3}|M{1,2}|D{1,2}|d{1,4}|H{1,2}|h{1,2}|m{1,2}|s{1,2}|S{1,3}|A|a)/g, 
    (match) => tokens[match] || match);
}

/**
 * Formats a number as currency according to the configured AmountFormat
 * @param {number} amount - The amount to format
 * @param {string} [format] - Optional format string (defaults to formatConfig.AmountFormat)
 * @returns {string} Formatted amount string
 */
export function formatAmount(amount, format) {
  const formatStr = format || formatConfig.AmountFormat || '#,###,###,##0.00';
  return applyNumberFormat(amount, formatStr);
}

/**
 * Formats a number as price according to the configured PriceFormat
 * @param {number} price - The price to format
 * @param {string} [format] - Optional format string (defaults to formatConfig.PriceFormat)
 * @returns {string} Formatted price string
 */
export function formatPrice(price, format) {
  const formatStr = format || formatConfig.PriceFormat || '#,###,###,##0.00';
  return applyNumberFormat(price, formatStr);
}

/**
 * Formats a number as quantity according to the configured QuantityFormat
 * @param {number} quantity - The quantity to format
 * @param {string} [format] - Optional format string (defaults to formatConfig.QuantityFormat)
 * @returns {string} Formatted quantity string
 */
export function formatQuantity(quantity, format) {
  const formatStr = format || formatConfig.QuantityFormat || '#,###,###,##0.000';
  return applyNumberFormat(quantity, formatStr);
}

/**
 * Gets the current format configuration
 * @returns {Object} The current format configuration
 */
export function getFormatConfig() {
  return { ...formatConfig };
}

// New function to format date and time (24-hour format)
export function formatDateTime24(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  
  // Format: MM-DD-YYYY HH:MM (24-hour)
  const pad = (n) => n.toString().padStart(2, '0');
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const year = d.getFullYear();
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  
  return `${month}-${day}-${year} ${hours}:${minutes}`;
}